<template>
  <div class="notes-card">
    <div class="notes-card__header">
      <div class="notes-card__title-wrap">
        <div class="notes-card__title">
          <i class="bi bi-chat-square-dots"></i>
          Recados
        </div>
        <label class="notes-card__switch">
          <input
            class="form-check-input"
            type="checkbox"
            role="switch"
            v-model="showArchived"
            @change="loadNotes()"
          >
          Arquivados
        </label>
      </div>
      <button
        v-if="hasPermission('Store Note') && !showArchived"
        type="button"
        class="notes-card__add"
        :class="{ 'is-open': inserting }"
        :title="inserting ? 'Cancelar' : 'Adicionar recado'"
        @click="inserting = !inserting"
      >
        <i :class="inserting ? 'bi bi-x-lg' : 'bi bi-plus-lg'"></i>
      </button>
    </div>
    <div class="notes-card__body">
      <PostIt v-if="inserting && !showArchived"
              key="new"
              title="Novo recado"
              :content="newNote"
              :readonly="false"
              :canArchive="true"
              @onStoreClick="insertNote"
              @onArchiveClick="inserting = false"
      />
      <PostIt v-for="note in notes"
              :key="note.id_note"
              :title="(note.user_name || 'Recado') + (note.created_at ? ' · ' + date(note.created_at).format('DD/MM/YYYY') : '')"
              :content="note.note"
              :readonly="true"
              :archived="showArchived"
              :canArchive="hasPermission('Delete Note')"
              :canRestore="hasPermission('Delete Note')"
              :canForceDelete="hasPermission('Delete Note')"
              @onArchiveClick="archiveNote(note.id_note)"
              @onRestoreClick="restoreNote(note.id_note)"
              @onForceDeleteClick="forceDeleteNote(note.id_note)"
      />
      <p v-if="!inserting && !notes.length" class="notes-card__empty">
        {{ showArchived ? 'Nenhum recado arquivado.' : 'Nenhum recado por enquanto.' }}
      </p>
    </div>

    <ScheduleDialog
      :open="forceDeleteId != null"
      title="Excluir permanentemente"
      @close="closeForceDelete"
    >
      <p class="notes-confirm__text">
        Excluir este recado permanentemente? Esta ação não pode ser desfeita.
      </p>
      <template #footer>
        <button type="button" class="notes-confirm__btn" :disabled="forceDeleting" @click="closeForceDelete">
          Cancelar
        </button>
        <button
          type="button"
          class="notes-confirm__btn is-danger"
          :disabled="forceDeleting"
          @click="confirmForceDelete"
        >
          {{ forceDeleting ? 'Excluindo…' : 'Excluir' }}
        </button>
      </template>
    </ScheduleDialog>
  </div>
</template>

<script>
import http from "../../helpers/http";
import PostIt from "../../components/PostIt.vue";
import ScheduleDialog from "./workspace/ScheduleDialog.vue";
import Alert from "../../helpers/alert";
import Permission from "../../helpers/permission";

export default {
  name: "Notes",
  props: ['profId', 'specId'],
  emits: ['change'],
  components: {
    PostIt,
    ScheduleDialog,
  },
  data() {
    return {
      notes: [],
      newNote: '',
      inserting: false,
      showArchived: false,
      forceDeleteId: null,
      forceDeleting: false,
    }
  },
  watch: {
    profId() {
      this.loadNotes();
    },
    specId() {
      this.loadNotes();
    },
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
  },
  mounted() {
    this.loadNotes();
  },
  methods: {
    date: moment,
    hasPermission(permission) {
      return Permission.has(permission);
    },
    notesUrl(id) {
      const base = `/places/${this.place.id_place}/professionals/${this.profId}/notes`;
      return id ? `${base}/${id}` : base;
    },
    loadNotes() {
      if (!this.profId) {
        return;
      }
      if (this.showArchived) {
        this.inserting = false;
      }
      return http.get(this.notesUrl(), {
        params: {
          include_trashed: this.showArchived,
        }
      }).then((response) => {
        this.notes = response.data;
      });
    },
    archiveNote(id) {
      return http.delete(this.notesUrl(id)).then(() => {
        Alert.toastSuccess('Recado arquivado com sucesso!');
        this.loadNotes();
        this.$emit('change');
      }).catch((error) => {
        Alert.toastError('Erro ao arquivar recado - ', error.response.data.message);
      });
    },
    restoreNote(id) {
      return http.post(`${this.notesUrl(id)}/restore`).then(() => {
        Alert.toastSuccess('Recado desarquivado com sucesso!');
        this.loadNotes();
        this.$emit('change');
      }).catch((error) => {
        Alert.toastError('Erro ao desarquivar recado - ', error.response.data.message);
      });
    },
    forceDeleteNote(id) {
      this.forceDeleteId = id;
      this.forceDeleting = false;
    },
    closeForceDelete() {
      if (this.forceDeleting) return;
      this.forceDeleteId = null;
    },
    confirmForceDelete() {
      if (this.forceDeleteId == null || this.forceDeleting) return;
      this.forceDeleting = true;
      return http.delete(`${this.notesUrl(this.forceDeleteId)}/force`).then(() => {
        this.forceDeleting = false;
        this.forceDeleteId = null;
        Alert.toastSuccess('Recado excluído permanentemente!');
        this.loadNotes();
        this.$emit('change');
      }).catch((error) => {
        this.forceDeleting = false;
        Alert.toastError('Erro ao excluir recado - ', error.response?.data?.message);
      });
    },
    insertNote(note) {
      if (!note)
        return Alert.toastWarning('Informe o recado.');

      return http.post(this.notesUrl(), {
        'note': note
      }).then(() => {
        Alert.toastSuccess('Recado incluído com sucesso!')
        this.inserting = false;
        this.loadNotes();

        this.$emit('change')
      }).catch((error) => {
        Alert.toastError('Erro ao incluir recado - ', error.response.data.message);
      });
    },
  },
}

</script>

<style scoped>
.notes-card {
  background: #fff;
  border: 1px solid #e4e6ef;
  border-radius: 0.85rem;
  overflow: hidden;
}
.notes-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 0.8rem;
  background: linear-gradient(180deg, #fbf8fa 0%, #fff 100%);
  border-bottom: 1px solid #f1eef3;
}
.notes-card__title-wrap {
  min-width: 0;
}
.notes-card__title {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.88rem;
  font-weight: 800;
  color: #181c32;
  line-height: 1.2;
}
.notes-card__title i {
  color: #780f5a;
  font-size: 0.95rem;
}
.notes-card__switch {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin: 0.35rem 0 0;
  font-size: 0.7rem;
  font-weight: 600;
  color: #7e8299;
  cursor: pointer;
}
.notes-card__switch .form-check-input {
  margin: 0;
  width: 1.55rem;
  height: 0.85rem;
  cursor: pointer;
}
.notes-card__add {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.15rem;
  height: 2.15rem;
  padding: 0;
  margin: 0;
  border: 1px solid #780f5a;
  border-radius: 0.55rem;
  background: #780f5a;
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 4px 10px rgba(120, 15, 90, 0.18);
}
.notes-card__add i {
  font-size: 1rem;
  line-height: 1;
}
.notes-card__add:hover {
  background: #65104c;
  border-color: #65104c;
  color: #fff;
}
.notes-card__add.is-open {
  background: #fff;
  color: #780f5a;
  box-shadow: none;
}
.notes-card__body {
  padding: 0.7rem 0.75rem 0.8rem;
  max-height: min(22rem, 50vh);
  overflow-y: auto;
  overflow-x: hidden;
}
.notes-card__empty {
  margin: 0.4rem 0 0.2rem;
  padding: 1.1rem 0.6rem;
  color: #a1a5b7;
  font-size: 0.78rem;
  font-weight: 600;
  text-align: center;
  background: #fafbfc;
  border: 1px dashed #e4e6ef;
  border-radius: 0.6rem;
}
.notes-confirm__text {
  margin: 0;
  color: #181c32;
  font-size: 0.92rem;
  font-weight: 600;
  line-height: 1.4;
}
.notes-confirm__btn {
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.4rem;
  margin: 0;
  padding: 0.45rem 0.95rem;
  border: 1px solid #e4e6ef;
  border-radius: 0.5rem;
  background: #fff;
  color: #181c32;
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.2;
  box-shadow: none;
  cursor: pointer;
}
.notes-confirm__btn:hover:not(:disabled) {
  border-color: #780f5a;
  color: #780f5a;
}
.notes-confirm__btn.is-danger {
  background: #780f5a;
  border-color: #780f5a;
  color: #fff;
}
.notes-confirm__btn.is-danger:hover:not(:disabled) {
  background: #65104c;
  border-color: #65104c;
  color: #fff;
}
.notes-confirm__btn:disabled {
  opacity: 0.65;
  cursor: default;
}
</style>
